// @MX:ANCHOR: [AUTO] 실물인증서 카드 조회(LOOKUP) FE 단일 공유 코어 — verify/Step3 공통 진입점
// @MX:REASON: [AUTO] SPEC-CHINA-CERT-CARD-LOOKUP-UNIFY-001 P1-6 — 이 코어는 "요청/정규화/디바운스(300ms)/race-guard"만
//   담당한다. 필드 조합, verify의 productName 클라이언트측 contains 필터, 화면별 고급필터는 각 화면(진입점)에 남긴다.
//   코어를 비대화하지 말 것(REQ-FE-CORE-004). fan_in >= 3 예상: verify(추후 리팩터)/Step3/e2e.
// @MX:SPEC: SPEC-CHINA-CERT-CARD-LOOKUP-UNIFY-001
//
// Vue 3.2.47 Options API 리포 컨벤션 — lodash 미사용, 수제 setTimeout 디바운스
// (참조: src/components/DealerGsp/DistinctRemoteSelect.vue, REQ-ARCH-004).
// Options API 컴포넌트에서도 그대로 사용 가능하도록 프레임워크-무관 plain factory로 작성한다.
//
// M0(Priority High): Step3용 FE 공유 코어만 준비. verify는 이 M0에서 건드리지 않는다(P1-7).

import certificateCardApi from '@/apis/certificateCard';

const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_MIN_LEN = 2;

/**
 * BE searchCards 응답 항목을 화면이 바로 쓸 수 있는 shape으로 정규화한다.
 * `used` 필드가 아직 없는 구버전 BE 응답(M0가 M1보다 먼저 배포)도 깨지지 않도록
 * 부재 시 false로 관용한다(REQ-FE-CORE-003).
 * @param {Object} raw - BE 응답 원본 항목
 * @returns {Object} 정규화된 카드 항목
 */
function normalizeCardItem(raw = {}) {
  return {
    randomNo: raw.randomNo || '',
    gtin: raw.gtin || '',
    productName: raw.productName || '',
    inboundBatchNumber: raw.inboundBatchNumber || '',
    batchId: raw.batchId || '',
    createdAt: raw.createdAt || null,
    batchStatus: raw.batchStatus || '',
    used: raw.used === true, // 부재 시 false 관용 (M0→M1 배포 순서 견딤)
    usedReason: raw.usedReason || '',
    matchedField: raw.matchedField || '', // 'randomNo' | 'gtin' | 'inboundBatchNumber' | ''
    raw,
  };
}

/**
 * 실물인증서 카드 검색 공유 코어를 생성한다.
 * 책임: 요청(searchFn 호출) / 정규화(normalizeCardItem) / 디바운스(300ms) / race-guard(stale 응답 폐기)만.
 * 필드 조합·고급필터·화면별 렌더링은 이 코어를 소비하는 각 화면(진입점)의 책임이다(P1-6).
 *
 * @param {Object} [options]
 * @param {Function} [options.searchFn] - (params) => Promise<AxiosResponse> — 기본은 certificateCardApi.searchCards
 * @param {number} [options.debounceMs=300] - 디바운스 지연(ms)
 * @param {number} [options.minLen=2] - 검색어 최소 길이(미만이면 요청 미발생)
 * @returns {{ search: Function, searchNow: Function, dispose: Function }}
 */
export function createCertCardSearch(options = {}) {
  const {
    searchFn = (params) => certificateCardApi.searchCards(params),
    debounceMs = DEFAULT_DEBOUNCE_MS,
    minLen = DEFAULT_MIN_LEN,
  } = options;

  let timer = null;
  let reqId = 0;
  let disposed = false;

  function clearTimer() {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }

  /**
   * BE 응답 payload에서 items 배열을 추출한다 (BE 응답 shape: { items, total, page, limit } 또는 { data: { items, ... } }).
   */
  function extractItems(res) {
    const payload = res?.data?.data ?? res?.data ?? {};
    return Array.isArray(payload.items) ? payload.items : [];
  }

  /**
   * 디바운스 없이 즉시 검색을 수행한다 (내부용, race-guard 적용).
   */
  async function runSearch(q, extraParams, callbacks) {
    const { onLoading, onResult, onError } = callbacks || {};
    const myReqId = ++reqId;
    if (onLoading) onLoading(true);
    try {
      const res = await searchFn({ q, ...extraParams });
      if (disposed || myReqId !== reqId) return; // stale 응답 폐기 (race-guard)
      const items = extractItems(res).map(normalizeCardItem);
      if (onResult) onResult(items);
    } catch (err) {
      if (disposed || myReqId !== reqId) return;
      if (onError) onError(err);
    } finally {
      if (!disposed && myReqId === reqId && onLoading) onLoading(false);
    }
  }

  /**
   * 통합 검색어(q) 기반 디바운스 검색. minLen 미만이면 요청 자체를 만들지 않는다(REQ-FE-CORE-002).
   * @param {string} q - 통합 검색어 (randomNo/gtin/inboundBatchNumber 부분일치 대상)
   * @param {Object} [extraParams] - page/limit 등 추가 파라미터 (필드 조합은 호출측 책임)
   * @param {Object} [callbacks]
   * @param {Function} [callbacks.onLoading] - (loading:boolean) => void
   * @param {Function} [callbacks.onResult] - (items:Array) => void
   * @param {Function} [callbacks.onError] - (err:Error) => void
   */
  function search(q, extraParams, callbacks) {
    clearTimer();
    const term = (q || '').trim();

    if (term.length < minLen) {
      // 짧은/빈 검색어 — 이전 요청도 무효화, 결과만 초기화(요청 미발생)
      reqId += 1;
      if (callbacks && callbacks.onResult) callbacks.onResult([]);
      return;
    }

    timer = setTimeout(() => {
      runSearch(term, extraParams, callbacks);
    }, debounceMs);
  }

  /**
   * 디바운스 없이 즉시 검색(예: 초기 로드, 페이지네이션 이동). minLen 가드는 동일 적용.
   */
  function searchNow(q, extraParams, callbacks) {
    clearTimer();
    const term = (q || '').trim();
    if (term.length < minLen) {
      reqId += 1;
      if (callbacks && callbacks.onResult) callbacks.onResult([]);
      return;
    }
    return runSearch(term, extraParams, callbacks);
  }

  /**
   * 컴포넌트 unmount 시 반드시 호출 — 대기 중인 디바운스 타이머를 정리하고 이후 응답을 모두 폐기한다.
   */
  function dispose() {
    disposed = true;
    clearTimer();
  }

  return { search, searchNow, dispose };
}

export default createCertCardSearch;
